/**
 * InviteLanding - Landing page for team invite links
 * v4.29 - 2026-10-13 - ROW 614/615 CORRECTION: without a Clerk ticket, the create-account form is
 *   never offered (an invited email needs the ticket to be shown and locked). The invite page
 *   views now are:
 *     - ticket in the URL → <InviteSetPassword /> (set-password view, invited email read-only)
 *     - signed in matching → Accept button
 *     - signed in mismatched → <InviteStopPage /> (unchanged)
 *     - signed out, accountExists true → single "Sign in to accept" button
 *     - signed out, accountExists false → translated outOfDate message, no form
 *     - signed out, accountExists absent (pre-session-401 server) → single "Sign in to accept"
 * v4.29 - 2026-10-13 - INVITE-ACCEPT-FLOW-01 (B.1, B.2, B.3 in the dispatch):
 *   - A ?__clerk_ticket=... in the URL replaces the invite page body with the set-password view
 *     (InviteSetPassword): the ticket brings the invited email in and signUp completes with no
 *     emailed code; the invite is accepted; the person lands in the inviting company.
 *   - Without a ticket, the server's `accountExists` decides the view (see above). invitePrimaryFor
 *     (services/api.ts) is the one place the rule lives.
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01 T4/T7, INVITE-LINK-SIGNED-IN-01 row 609):
 *   - A signed-out person is sent to sign in or sign up and comes back to the same invite (return path).
 *   - Signed in with a DIFFERENT email than the invite: the stop page (signed-in account, masked invited
 *     email, "Sign out and continue", "Go to my dashboard"). Never the full invited email. Nothing is used up on view.
 *   - Accept shows the server's reason in words (400/403/404/409/410/500 codes) and, on success, makes the invited
 *     company the chosen company and opens the dashboard.
 *   - All texts come from the translation file (common: teamAccess.*).
 *   - Also exports InviteStopPage and SignedInInviteGate, used by the /register route for pilot and team invite links.
 * v2.31 - 2026-01-31 - Initial implementation
 *
 * When a user clicks a team invite link in their email, they land here.
 * This page:
 * 1. Validates the invite token
 * 2. Shows info about the team/role
 * 3. Allows them to accept the invite
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BusinessIcon from '@mui/icons-material/Business';
import { teamService, pilotInviteService, InviteValidationResponse, inviteViewFor, inviteGateViewFor, invitePrimaryFor, InviteGateView } from '../services/api';
import { rememberReturnPath } from '../services/authGate';
import { setChosenCompany, logClientDecision } from '../services/portalRequest';
import InviteSetPassword from './InviteSetPassword'; // v4.29 - INVITE-ACCEPT-FLOW-01 (B.1): the ticket set-password view

export type InviteKind = 'pilot' | 'team';

interface StopPageProps {
  kind: InviteKind;
  /** The invitation's id from the server; logged with every decision (never an email). */
  inviteId?: string;
  maskedEmail?: string;
  /** The link the person opened; "Sign out and continue" returns here. */
  returnTo: string;
}

/**
 * Row 609: a person signed in as a DIFFERENT email opened an invite link.
 * Shows who is signed in and a masked version of the invited email; the person decides.
 */
export const InviteStopPage: React.FC<StopPageProps> = ({ kind, inviteId, maskedEmail, returnTo }) => {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { user } = useUser();
  const [working, setWorking] = useState(false);
  const signedInAccount = user?.primaryEmailAddress?.emailAddress || '';

  useEffect(() => {
    logClientDecision('invite_stop_page_shown', { kind, invite: inviteId || 'unknown' });
  }, [kind, inviteId]);

  const handleSignOutAndContinue = async () => {
    setWorking(true);
    logClientDecision('invite_sign_out_and_continue', { kind, invite: inviteId || 'unknown' });
    try {
      await signOut();
    } finally {
      // back to the same link; signed out, it opens as a normal invite link
      window.location.assign(returnTo);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <GroupAddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {t('teamAccess.stop.title')}
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('teamAccess.stop.body', { signedIn: signedInAccount, invited: maskedEmail || '' })}
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="contained" onClick={handleSignOutAndContinue} disabled={working}>
              {working ? <CircularProgress size={22} color="inherit" /> : t('teamAccess.stop.signOutAndContinue')}
            </Button>
            <Button variant="outlined" href="/dashboard" disabled={working}>
              {t('teamAccess.stop.goToDashboard')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

/**
 * The /register route for a SIGNED-IN person who opened a pilot (?invite=CODE) or team (?teamInvite=TOKEN) link.
 * The server compares the invite email with the signed-in email; nothing is consumed by looking.
 */
export const SignedInInviteGate: React.FC<{ kind: InviteKind; credential: string }> = ({ kind, credential }) => {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [state, setState] = useState<'checking' | InviteGateView>('checking');
  const [maskedEmail, setMaskedEmail] = useState<string | undefined>(undefined);
  const [inviteId, setInviteId] = useState<string | undefined>(undefined);

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const token = await getToken();
        const result: InviteValidationResponse = kind === 'pilot'
          ? await pilotInviteService.validate(credential, token)
          : await teamService.validateInvite(credential, token);
        if (!active) return;
        setMaskedEmail(result.maskedEmail);
        setInviteId(result.inviteId);
        const view = inviteGateViewFor(kind, result);
        logClientDecision('invite_gate_decided', { kind, view, invite: result.inviteId || 'unknown' });
        setState(view);
      } catch {
        if (active) setState('not_valid');
      }
    };
    check();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, credential]);

  const returnTo = kind === 'pilot'
    ? `/register?invite=${encodeURIComponent(credential)}`
    : `/register?teamInvite=${encodeURIComponent(credential)}`;

  if (state === 'checking') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }
  if (state === 'stop') {
    return <InviteStopPage kind={kind} inviteId={inviteId} maskedEmail={maskedEmail} returnTo={returnTo} />;
  }
  if (state === 'go_to_invite') {
    // the invited person is signed in: the invite page is where they accept
    return <Navigate to={`/invite/${credential}`} replace />;
  }
  if (state === 'account_exists') {
    // pilot invite for the signed-in account: the account exists; offer the dashboard (no silent redirect)
    logClientDecision('invite_same_account_shown', { kind, invite: inviteId || 'unknown' });
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 3 }}>{t('teamAccess.stop.sameAccount')}</Typography>
            <Button variant="contained" href="/dashboard">{t('teamAccess.stop.goToDashboard')}</Button>
          </Paper>
        </Container>
      </Box>
    );
  }
  // The link is not valid any more: say so and still offer the dashboard (never a silent redirect)
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>{t('teamAccess.stop.linkNotValid')}</Alert>
          <Button variant="contained" href="/dashboard">{t('teamAccess.stop.goToDashboard')}</Button>
        </Paper>
      </Container>
    </Box>
  );
};

const InviteLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoaded: authLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [status, setStatus] = useState<InviteValidationResponse | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const shownLogged = useRef(false);

  // Check for success in URL params (after accepting)
  const urlSuccess = searchParams.get('success');
  const urlError = searchParams.get('error');

  useEffect(() => {
    if (token && authLoaded) {
      checkStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authLoaded, isSignedIn]);

  const checkStatus = async () => {
    if (!token) return;

    setLoading(true);
    try {
      // signed in: send the token so the server can also say whether the invite is for this account
      const authToken = isSignedIn ? await getToken() : null;
      const result = await teamService.validateInvite(token, authToken);
      setStatus(result);
    } catch (err: any) {
      setStatus({
        valid: false,
        error: err.userMessage || t('teamAccess.invite.validateFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  const goToSignIn = (target: '/login' | 'register') => {
    if (!token) return;
    // The invite rides through sign-in / sign-up and AuthLanding brings the person back here.
    rememberReturnPath(`/invite/${token}`);
    logClientDecision('invite_sent_to_sign_in', { kind: 'team', target: target === '/login' ? 'sign_in' : 'sign_up', invite: status?.inviteId || 'unknown' });
    navigate(target === '/login' ? '/login' : `/register?teamInvite=${encodeURIComponent(token)}`);
  };

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setAcceptError(null);
    // a 401 signs the person out; the return path brings them back to this invite after signing in again
    rememberReturnPath(`/invite/${token}`);
    try {
      const result = await teamService.acceptInvite(token);
      if (result.success && result.tenantUuid) {
        // the invited company becomes the chosen company; the dashboard opens in it
        setChosenCompany(result.tenantUuid, user?.id || null);
        logClientDecision('invite_accepted_company_chosen', { company: result.tenantUuid, invite: status?.inviteId || 'unknown' });
        window.location.assign('/dashboard');
        return;
      }
      setAcceptError(t(`teamAccess.acceptErrors.${result.error || 'default'}`, { defaultValue: t('teamAccess.acceptErrors.default') }));
    } catch (err: any) {
      // Honest status codes: the reason code is shown in words, never raw server text
      const code = err?.response?.data?.error;
      logClientDecision('invite_accept_refused', { reason: code || `http_${err?.response?.status || 'none'}`, invite: status?.inviteId || 'unknown' });
      setAcceptError(t(`teamAccess.acceptErrors.${code || 'default'}`, { defaultValue: t('teamAccess.acceptErrors.default') }));
    } finally {
      setAccepting(false);
    }
  };

  // Success state
  if (urlSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              {t('teamAccess.invite.welcomeTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('teamAccess.invite.welcomeBody')}
            </Typography>
            <Button variant="contained" href="/login" sx={{ bgcolor: 'primary.main' }}>
              {t('teamAccess.invite.signIn')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Error state from URL
  if (urlError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              {t('teamAccess.invite.couldNotJoin')}
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {t(`teamAccess.acceptErrors.${urlError}`, { defaultValue: t('teamAccess.acceptErrors.default') })}
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Loading state
  if (loading || !authLoaded) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  const view = inviteViewFor(status, !!isSignedIn);
  if (!shownLogged.current) {
    shownLogged.current = true;
    logClientDecision('invite_page_shown', { kind: 'team', view, invite: status?.inviteId || 'unknown' });
  }

  // v4.29 - INVITE-ACCEPT-FLOW-01 (B.1): a ?__clerk_ticket= in the URL replaces the whole invite
  // body with the set-password view. The ticket only means something when the invite is valid;
  // on an invalid or expired link the "invalid" branch below runs instead and the ticket is ignored.
  const clerkTicket = searchParams.get('__clerk_ticket');
  if (clerkTicket && status && status.valid && token) {
    return <InviteSetPassword token={token} ticket={clerkTicket} status={status} />;
  }

  // Invalid or expired link
  if (view === 'invalid') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              {t('teamAccess.invite.invalidTitle')}
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {status?.reason
                ? t(`teamAccess.acceptErrors.${status.reason}`, { defaultValue: t('teamAccess.invite.invalidBody') })
                : t('teamAccess.invite.invalidBody')}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {t('teamAccess.invite.askOwner')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Row 609: signed in as a different email than the invite - stop page, nothing is used up
  if (view === 'stop' && status) {
    return (
      <InviteStopPage
        kind="team"
        inviteId={status.inviteId}
        maskedEmail={status.maskedEmail}
        returnTo={`/invite/${token}`}
      />
    );
  }

  if (!status) return null; // unreachable: 'invalid' above covers a missing answer
  const roleName = t(`teamAccess.roles.${status.role || ''}`, { defaultValue: status.role || '' });

  // Valid invite
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#003366', py: 2, px: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
          MyLojic
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <GroupAddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {t('teamAccess.invite.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('teamAccess.invite.subtitle')}
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ mb: 4, bgcolor: '#f9f9f9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {status.companyName || t('teamAccess.company.unnamed')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('teamAccess.invite.roleLabel')} <strong>{roleName}</strong>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('teamAccess.invite.accessNote', { company: status.companyName || '', role: roleName })}
            </Typography>
          </Alert>

          {acceptError && (
            <Alert severity="error" sx={{ mb: 3 }}>{acceptError}</Alert>
          )}

          {view === 'accept' ? (
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleAccept}
              disabled={accepting}
              sx={{ bgcolor: '#008080', py: 1.5, fontSize: '1.1rem', '&:hover': { bgcolor: '#006666' } }}
            >
              {accepting ? <CircularProgress size={24} color="inherit" /> : t('teamAccess.invite.accept')}
            </Button>
          ) : (
            // v4.29 - 2026-10-13 - ROW 614/615 correction: without a Clerk ticket the create-account
            // path is NOT offered — a new account would need the invited email to be shown and
            // locked, and only a ticket brings it in. The ticket branch is handled above by rendering
            // <InviteSetPassword />, so by the time we reach this block there is no ticket.
            //   invitePrimaryFor is still the one place the rule lives:
            //     'create_account' → the person has no account → the outOfDate message (no form)
            //     'sign_in' | 'exists_unknown' → single "Sign in to accept" button (pre-session-401
            //     servers return no accountExists and the sign-in default stays safe)
            (() => {
              const primary = invitePrimaryFor(status);
              if (primary === 'create_account') {
                return (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t('teamAccess.invite.outOfDate')}
                  </Alert>
                );
              }
              return (
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => goToSignIn('/login')}
                  sx={{ bgcolor: '#008080', py: 1.5, '&:hover': { bgcolor: '#006666' } }}
                >
                  {t('teamAccess.invite.signInToAccept')}
                </Button>
              );
            })()
          )}

          {status.expiresAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              {t('teamAccess.invite.expires', { date: new Date(status.expiresAt).toLocaleDateString() })}
            </Typography>
          )}
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Powered by <strong>MyLojic</strong> - AI-native analytics for SMBs
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default InviteLanding;
